import type {
  RegisteredRouter,
  ValidateRedirectOptions,
} from '@tanstack/react-router';

import type { Lead } from 'providers/LeadProvider';
import type { StatusScreen } from 'router/params/statusScreen';

export type ValidateLeadStatusReturn<
  TRouter extends RegisteredRouter = RegisteredRouter,
  TOptions = unknown,
> =
  | {
      shouldRedirect: true;
      redirectOptions: ValidateRedirectOptions<TRouter, TOptions>;
    }
  | { shouldRedirect: false };

const noRedirect: ValidateLeadStatusReturn = {
  shouldRedirect: false,
};

const redirectToStatus: ValidateLeadStatusReturn = {
  shouldRedirect: true,
  redirectOptions: { to: '/status', replace: true },
};

const leadStatusReasonToStatusScreen = {
  former_partner: 'former-partner',
  gym_temporarily_permanently_closed: 'former-partner',
  lost: 'former-partner',
  signed_contract: 'former-partner',
  partner_setup: 'former-partner',
  launched_won: 'former-partner',
  ss_ongoing_opp: 'in-review',
  no_decision_no_reply: 'in-review',
  not_willing_to_discuss: 'in-review',
  offer_sent: 'in-review',
  offer_approved: 'in-review',
  in_negotiation: 'in-review',
  qualification: 'in-review',
  appointment_based_model_not_supported_by_gympass_business_model:
    'unsupported-business-type',
  is_not_a_gym: 'unsupported-business-type',
  works_with_a_competitor: 'unsupported-business-type',
  incorrect_data_data_not_found: 'not-eligible',
  fraud: 'not-eligible',
  already_a_partner: 'already-a-partner',
  ss_tax_id_issue: 'tax-id-issue',
} as const satisfies Record<NonNullable<Lead['statusReason']>, StatusScreen>;

/**
 * The single source of truth for which Status screen (if any) a Lead maps
 * to. Replaces the old validateLeadStatus/validateStatusPageAccess pair: one
 * pure function instead of two hand-kept-inverse ones, so the funnel guard
 * and the rendered screen can never drift apart.
 *
 * `null` means the Lead belongs in the funnel, not on a Status screen.
 */
export function resolveStatusScreen(
  status: Lead['status'],
  statusReason: Lead['statusReason'],
): StatusScreen | null {
  if (!status || status === 'ongoing' || status === 'to_be_qualified') {
    return null;
  }

  if (status === 'wishlist') {
    return 'waitlist';
  }

  if (status === 'blocklist' || (status === 'rejected' && !statusReason)) {
    return 'not-eligible';
  }

  if (status === 'converted' && !statusReason) {
    return 'in-review';
  }

  if (!statusReason || !(statusReason in leadStatusReasonToStatusScreen)) {
    return 'in-review';
  }

  return leadStatusReasonToStatusScreen[statusReason];
}

/**
 * Validates lead status and determines if redirection to the Status page is
 * needed. Terminal statuses (blocklist, converted, rejected, wishlist)
 * redirect to `/status`; ongoing/to_be_qualified leads continue the funnel.
 */
export function validateLeadStatus(
  leadStatus: Lead['status'],
  leadStatusReason: Lead['statusReason'],
): ValidateLeadStatusReturn {
  return resolveStatusScreen(leadStatus, leadStatusReason) === null
    ? noRedirect
    : redirectToStatus;
}

/**
 * Builds a gate that behaves like validateLeadStatus except that a rejected
 * Lead with one of the given Status reasons is allowed through: each of
 * those reasons Field-restores into (or otherwise navigates back into) the
 * guarded step. The Lead working copy only converges with Salesforce at Lead
 * recovery, so a session-restored (still rejected) Partner must be able to
 * land on the step directly instead of bouncing through their Status screen
 * to click retry first.
 */
function validateLeadStatusAllowingRejection(
  restorableReasons: NonNullable<Lead['statusReason']>[],
) {
  return function validateRestorableLeadStatus(
    leadStatus: Lead['status'],
    leadStatusReason: Lead['statusReason'],
  ): ValidateLeadStatusReturn {
    if (
      leadStatus === 'rejected' &&
      leadStatusReason &&
      restorableReasons.includes(leadStatusReason)
    ) {
      return noRedirect;
    }

    return validateLeadStatus(leadStatus, leadStatusReason);
  };
}

/**
 * `is_not_a_gym` is the rejection whose Field restore (on the unsupported
 * business type screen) jumps into business-details. `already_a_partner` is
 * the rejection whose Status screen CTA navigates straight back into
 * business-details so the Partner can pick the right existing location.
 */
export const validateBusinessDetailsLeadStatus =
  validateLeadStatusAllowingRejection(['is_not_a_gym', 'already_a_partner']);

/**
 * `ss_tax_id_issue` is the one rejection whose Field restore (on the
 * invalid tax ID screen) jumps into tax-details.
 */
export const validateTaxDetailsLeadStatus = validateLeadStatusAllowingRejection(
  ['ss_tax_id_issue'],
);

/**
 * Only leads with 'converted' status are allowed access to the success page.
 * Everything else delegates to validateLeadStatus.
 */
export function validateSuccessPageAccess(
  leadStatus: Lead['status'],
  leadStatusReason: Lead['statusReason'],
): ValidateLeadStatusReturn {
  if (leadStatus === 'converted') {
    return noRedirect;
  }

  return validateLeadStatus(leadStatus, leadStatusReason);
}
